import { Box } from '@mui/material'

import { BodyText } from '@/components/atoms/BodyText'
import { MedicationPlaceholderText } from '@/apps/latest/components/molecules/MedicationPlaceholderText'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { DURATION } from '@/shared/styles/motion'

import { EditInputBase } from './EditInputBase'
import { SoapLineBulletSlot } from './SoapLineBulletSlot'

interface Props {
  // 行の DOM id (変わった行へ scroll する先。付けない view は省略)
  id?: string
  content: string
  isStruck: boolean
  // AI で改善して変わった行の印 (外れると地が fade-out で戻る)
  isFlashing?: boolean
  isEditing: boolean
  editingText: string
  onEditingTextChange: (value: string) => void
  onToggleStrike: () => void
  onStartEdit: () => void
  onEditBlur: () => void
  onEditEnter: () => void
  onEditEscape: () => void
  medications: string[]
  onPlaceholderReplace: (
    placeholder: string,
    replacement: string,
    replaceAll?: boolean
  ) => Promise<boolean>
}

// SOAP 本文の 1 行. 行頭の常時表示チェックボックスがコピー対象の採否 (= 取消線) を担い、
// 本文タップで inline 編集に入る (FB MTG 2026-06-30 での要望に基づく role. 詳細:
// log/171_20260701_soap_checklist_notion_checkbox_redesign/work-log/01 §3.1).
export const SoapLine = ({
  id,
  content,
  isStruck,
  isFlashing = false,
  isEditing,
  editingText,
  onEditingTextChange,
  onToggleStrike,
  onStartEdit,
  onEditBlur,
  onEditEnter,
  onEditEscape,
  medications,
  onPlaceholderReplace,
}: Props) => {
  // 編集中はチェックボックス固定スロット + 枠なし入力 (= 表示と同じ字格・本文 x). slot と入力は
  // 新規行 (SoapBody) と共用 component で字格ドリフトを防ぐ. 編集中だけ淡い地色で対象行を示す.
  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: `${RADIUS.sm}px`,
          bgcolor: colorTokens.overlay.selected,
        }}
      >
        <SoapLineBulletSlot checked={!isStruck} />
        <EditInputBase
          value={editingText}
          onChange={onEditingTextChange}
          onBlur={onEditBlur}
          onKeyDown={(e) => {
            // IME 変換中の Enter は確定操作なので行の確定に使わない (かな→漢字の確定で
            // 行が保存され、 preventDefault が変換の確定自体を妨げる)。
            if (
              e.key === 'Enter' &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault()
              onEditEnter()
            }
            if (e.key === 'Escape') {
              onEditEscape()
            }
          }}
        />
      </Box>
    )
  }

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        borderRadius: `${RADIUS.sm}px`,
        // 行内に縦の余白を持たせ、 当たり判定を ~Notion 行 (30px) に近づける. 隣接行を
        // 取り違えにくくし、 密に詰めすぎない (正典 §4.1: 行は詰めず段落間 2–4px + 行に air).
        py: 0.25,
        bgcolor: isFlashing ? colorTokens.state.warnBg : undefined,
        transition: `background-color ${DURATION.fast}ms`,
        '&:hover': {
          bgcolor: isFlashing
            ? colorTokens.state.warnBg
            : colorTokens.overlay.hover,
        },
      }}
    >
      {/* 行頭の常時表示チェックボックス (= コピー対象の採否トグル). 固定幅
          (SOAP_LINE_BULLET_WIDTH) の slot なので全行で同じ x に整列する
          (= UI のエントロピー最小化). 本文 1 行目の高さで縦中央寄せするため
          SoapLineBulletSlot 側で本文とのズレを吸収する. */}
      <SoapLineBulletSlot
        checked={!isStruck}
        onToggle={onToggleStrike}
        ariaLabel={isStruck ? 'コピー対象に戻す' : 'コピー対象から除外'}
      />
      {/* role="button" の div (= 本文中の薬剤名 placeholder が独自の onClick を持つため、
          nested interactive content を禁じる native <button> にはできない). キーボード
          操作 (Tab → Enter/Space) を role="button" だけに頼らず tabIndex + onKeyDown で
          明示的に再実装する (実機確認: role="button" だけでは Tab で到達できず、 編集の
          唯一の入口が失われていた). */}
      <Box
        role="button"
        tabIndex={0}
        aria-label="この行を編集"
        onClick={onStartEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onStartEdit()
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          userSelect: 'none',
          pr: 0.5,
          borderRadius: `${RADIUS.sm}px`,
          '&:focus': { outline: 'none' },
          '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
        }}
      >
        <BodyText
          sx={{
            whiteSpace: 'pre-wrap',
            textDecoration: isStruck ? 'line-through' : 'none',
            color: isStruck ? colorTokens.fg.faint : undefined,
          }}
        >
          <MedicationPlaceholderText
            text={content}
            medInfo={medications}
            onReplace={onPlaceholderReplace}
          />
        </BodyText>
      </Box>
    </Box>
  )
}
